import { defaultAppConfig, setAppConfig } from '@shared-core/features/config'
import { selectedApiBaseUrlOverride, selectedUserThemePalette, setDefaultLang } from '@shared-core/features/settings'
import {
  setApiBaseUrlOverride,
  setApiMockBaseUrlOverride,
  setApiOriginHint,
  setApiPlatform,
  setEnvStore,
} from '@shared-core/services/api'
import { type AppDispatch, createStore, migrationStore, Provider, sharedCoreWhitelist } from '@shared-core/store'
import { AppStartupTransition } from '@shared-ui/components/app/startup-transition'
import { NotificationsProvider } from '@shared-ui/components/service/notification/provider'
import { App } from '@web/app'
import { WebSafeAreaProvider } from '@web/components/safe-area-provider'
import { ConfirmDialogHost } from '@web/components/service/confirm-dialog'
import { NotificationHost } from '@web/components/service/notification-host'
import { ServiceWebBoot } from '@web/components/service/web-boot'
import { getWebLocaleFormatted, getWebLocaleISO } from '@web/libs/device-locale'
import { resolveWebApiPlatform } from '@web/libs/platform/api-platform'
import { bootstrapTmaSession } from '@web/libs/tg/bootstrap-session'
import { restoreStore, watcher } from '@web/store/persist'
import { applyCssThemeVariables } from '@web/themes/apply-css-theme-variables'
import { resolveWebColorPalette } from '@web/themes/resolve-web-color-palette'
import { SharedUiThemeProvider } from '@web/themes/shared-ui-provider'

const store = createStore(migrationStore(restoreStore()))
setApiPlatform(resolveWebApiPlatform())

applyCssThemeVariables(resolveWebColorPalette(selectedUserThemePalette(store.getState())))

watcher(store, sharedCoreWhitelist)
setEnvStore(store)

const apiBaseUrl = import.meta.env.VITE_API_BASE_URL?.trim() || null

if (apiBaseUrl) {
  setApiBaseUrlOverride(apiBaseUrl)
} else if (import.meta.env.DEV) {
  // Пустой baseURL — запросы на origin, Vite проксирует /api (см. vite.config.ts).
  setApiBaseUrlOverride('')
  // Прокси прячет реальный стейдж — подсказываем его origin для URL поддоменов окружения
  setApiOriginHint(import.meta.env.VITE_API_PROXY_TARGET?.trim() || null)
}

// Dev-тумблер "Мок-бэк": переживает логаут и перезагрузку.
const storedApiBaseUrlOverride = selectedApiBaseUrlOverride(store.getState())

if (storedApiBaseUrlOverride) {
  setApiMockBaseUrlOverride(storedApiBaseUrlOverride)
}

if (import.meta.env.DEV) {
  // @ts-expect-error debug helper for web devtools
  globalThis.store = store
}

// Инициализирует web-store: конфиг и локаль (синхронно с первым рендером).
async function bootstrapStore() {
  await store.dispatch(setAppConfig(defaultAppConfig))

  await (store.dispatch as AppDispatch)(
    setDefaultLang({
      locale: getWebLocaleFormatted(),
      localeISO: getWebLocaleISO(),
    }),
  )

  await bootstrapTmaSession(store.dispatch)
}

// Повторное монтирование в StrictMode не должно создавать вторую сессию.
let bootstrapPromise: Promise<void> | null = null

function initStore(): Promise<void> {
  bootstrapPromise ??= bootstrapStore()
  return bootstrapPromise
}

function hideStaticSplash() {
  document.getElementById('static-splash')?.remove()
}

export function RootApp() {
  // ThemeProvider снаружи ServiceWebBoot: иначе fallback до ready без темы → светлая вспышка.
  return (
    <Provider store={store}>
      <SharedUiThemeProvider>
        <ServiceWebBoot init={initStore}>
          <WebSafeAreaProvider>
            <NotificationsProvider>
              <AppStartupTransition onVisible={hideStaticSplash}>
                <App />
              </AppStartupTransition>
              <ConfirmDialogHost />
              <NotificationHost />
            </NotificationsProvider>
          </WebSafeAreaProvider>
        </ServiceWebBoot>
      </SharedUiThemeProvider>
    </Provider>
  )
}
